/**
 * Triangulation — client-side shared types (DC-1492).
 *
 * Mirrors the server `server/triangulation/types.ts` shapes returned from the
 * `/api/triangulation/*` routes. Kept as a deliberate copy rather than a
 * cross-folder import so the client bundle never picks up server-only code.
 * If the server type ever moves out of lock-step with the response shape,
 * fix it here too.
 *
 * Spec: Documentation/Development/Design Docs/Sprint 15/Triangulated_FAIR_Assessment_Design.md
 */

export const FAIR_PILLAR_NAMES = [
  'Findable',
  'Accessible',
  'Interoperable',
  'Reusable',
] as const;

export type FairPillarName = (typeof FAIR_PILLAR_NAMES)[number];

export const LEG_STATES = [
  'present_and_scored',
  'deliberately_absent',
  'not_yet_gathered',
] as const;

export type LegState = (typeof LEG_STATES)[number];

export const COVERAGE_SUFFICIENCY_LEVELS = [
  'sufficient',
  'preliminary',
  'insufficient',
] as const;

export type CoverageSufficiency = (typeof COVERAGE_SUFFICIENCY_LEVELS)[number];

/** v2.5 SS-03 C2 — what the Perceived denominator IS (G0 §5.2). */
export const COVERAGE_BASES = ['unreviewed', 'too_few_contributors', 'confirmed'] as const;
export type CoverageBasis = (typeof COVERAGE_BASES)[number];

export interface LegCoverage {
  evaluated: number;
  total: number;
  /** null when the basis has no legitimate denominator (unreviewed /
   *  too_few_contributors) — a percentage always names its denominator. */
  pct: number | null;
  sufficiency: CoverageSufficiency;
  /** Perceived leg only (C2, migration 140). */
  basis?: CoverageBasis;
}

export interface LegReading {
  state: LegState;
  score: number | null;
  coverage: LegCoverage;
}

export const PATTERN_NAMES = [
  'Usability Gap',
  'Tribal Knowledge',
  'Unformalized Practice',
  'Abandoned Capability',
  'Policy Theater',
  'Confirmed Maturity',
  'Greenfield',
  'Aligned Maturity',
] as const;

export type PatternName = (typeof PATTERN_NAMES)[number];

/**
 * v2.5 SS-03 (C1): confidence is TWO INDEPENDENT dimensions. The legacy
 * single word is DERIVED server-side ('provisional' is NEW: full/declared-
 * absent basis with preliminary contributing coverage — previously
 * mislabeled 'confirmed'). Mirrors server/triangulation/types.ts.
 */
export type EvidenceBasis = 'full' | 'documented_declared_absent' | 'documented_missing';
export type CoverageConfidence = 'sufficient' | 'preliminary';
export type PatternQualifier = 'confirmed' | 'provisional' | 'suspected' | null;

/**
 * Role-partitioned Perceived reading (DC-1612 — FAIR-MM Phase 1). Mirrors
 * `server/triangulation/types.ts:RolePerceptionReading`. Present on a cell
 * only when the fetch requested `?roleBreakdown=true`.
 */
export type RoleGapState =
  | 'aligned'
  | 'producer_optimistic'
  | 'consumer_optimistic'
  | 'indeterminate';

export type RoleAttributionBasis = 'contextual' | 'derived' | 'mixed';

export interface RolePerceptionReading {
  producer: LegReading;
  consumer: LegReading;
  enabler: LegReading;
  /** producer.score − consumer.score on [0, 5]; null unless both sides scored. */
  gap: number | null;
  gapState: RoleGapState;
  attribution: RoleAttributionBasis | null;
}

export interface TriangulationCell {
  pillar: FairPillarName;
  technical: LegReading;
  perceived: LegReading;
  documented: LegReading;
  coherence: number | null;
  pattern: PatternName | null;
  /** LEGACY derived word — the UI composes from the two dimensions below. */
  pattern_qualifier: PatternQualifier;
  /** DC-1612: present only when the fetch requested `?roleBreakdown=true`. */
  rolePerception?: RolePerceptionReading;
  /** Null exactly when `pattern` is null. */
  evidence_basis: EvidenceBasis | null;
  coverage_confidence: CoverageConfidence | null;
}

/**
 * Consultant-asserted documented-leg state for a domain (DC-1572 migration 106).
 * v1 emits exactly one non-NULL value (`deliberately_absent`); NULL means
 * unasserted — the documented leg derives state from contributing docs.
 *
 * Mirrors `server/triangulation/types.ts:DOCUMENTED_STATES`.
 */
export const DOCUMENTED_STATES = ['deliberately_absent'] as const;
export type DocumentedState = (typeof DOCUMENTED_STATES)[number];

export interface Domain {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  description: string | null;
  /**
   * Consultant-asserted documented-leg state. NULL = unasserted (the
   * documented leg derives state from contributing docs).
   * `'deliberately_absent'` = consultant has confirmed no documented signal
   * for this domain — leg renders as deliberately_absent across all pillars.
   * Set via `PATCH /api/domains/:id/documented-state`.
   */
  documented_state: DocumentedState | null;
  /** v2.5 SS-03 (C1): attribution snapshot of the CURRENT assertion — the
   *  declaration is evidence, so it names who made it and when. Null when
   *  unasserted. Mirrors the server contract. */
  documented_state_set_by: string | null;
  documented_state_set_by_name: string | null;
  documented_state_set_by_role: string | null;
  documented_state_set_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DomainSummary extends Domain {
  dataset_count: number;
  interview_count: number;
  document_count: number;
}

/** The classification bands the confidence dimensions were computed with
 *  (v2.5 SS-03) — shipped on the wire so no client hardcodes them. */
export interface TriangulationThresholds {
  qualifier_methodology_version: string;
  pattern: { high: number; low: number };
  technical: { hard_floor_datasets: number; preliminary_below_pct: number };
  perceived: { hard_floor_personas: number; preliminary_below_pct: number };
  /** C2 (migration 140): the coverage denominator counts — confirmed
   *  targets is THE denominator; not_relevant excluded; any unreviewed
   *  blocks Sufficient. */
  perceived_targets?: {
    denominator: 'confirmed_targets';
    confirmed: number;
    unreviewed: number;
    not_relevant: number;
    total_mapped: number;
  };
}

// ============================================================================
// Persona targets (v2.5 SS-03 C2, migration 140)
// ============================================================================

export const PERSONA_TARGET_STATUSES = ['unreviewed', 'confirmed', 'not_relevant'] as const;
export type PersonaTargetStatus = (typeof PERSONA_TARGET_STATUSES)[number];

/** One row of a domain's curated persona target population. Status is a
 *  STATE, never a deletion; changes are admin-only and audited
 *  (persona_target_status_changed). Reconciliation invariant:
 *  confirmed + unreviewed + not_relevant = total mapped. */
export interface PersonaTarget {
  id: string;
  org_id: string;
  domain_id: string;
  persona_id: string;
  persona_name: string;
  persona_archetype: string | null;
  status: PersonaTargetStatus;
  status_set_by: string | null;
  status_set_at: string | null;
  status_reason: string | null;
  source: 'seed' | 'admin';
  added_at: string;
}

export interface PersonaTargetCounts {
  confirmed: number;
  unreviewed: number;
  not_relevant: number;
  total: number;
}

export interface DomainTriangulation {
  domain: Domain;
  pillars: TriangulationCell[];
  metadata: {
    dataset_count: number;
    interview_count: number;
    document_count: number;
    last_updated: string | null;
    thresholds: TriangulationThresholds;
  };
}

// ============================================================================
// Persona entity (DC-1572 migration 106 + DC-1606 migration 109)
// ----------------------------------------------------------------------------
// Mirrors `server/triangulation/types.ts` Persona shape. Kept as a copy so
// the client bundle never imports server code.
// ============================================================================

/**
 * Source of a persona row.
 *   - `'pistoia'` — seeded from the Pistoia FAIR Maturity Matrix catalog by
 *     `server/db/seedPistoiaPersonas.ts`. Rich Pistoia fields populated.
 *   - `'custom'`  — consultant-authored via the FairPersonasAdmin UI (DC-1605),
 *     or a legacy-only governance persona carried by migration 112 (DC-1648).
 *     Rich Pistoia fields may be NULL.
 *   - `'compass'` — Compass-curated persona outside the Pistoia catalog
 *     (DC-1648: Document_Owner).
 *   - NULL        — pre-migration-109 row. Same surface as `'custom'`.
 */
export const PERSONA_SOURCES = ['pistoia', 'custom', 'compass'] as const;
export type PersonaSource = (typeof PERSONA_SOURCES)[number];

/** Pistoia role grouping (DC-1648). Drives topology clustering/coloring. */
export const PRIMARY_ROLES = ['Data Producer', 'Data Enabler', 'Data Consumer'] as const;
export type PrimaryRole = (typeof PRIMARY_ROLES)[number];

/**
 * One entry in a persona's cross-persona graph. NOT a FK relationship — the
 * Pistoia upstream catalog has dangling slug references. The UI resolves
 * known slugs to in-app links and renders unknown slugs as plain text.
 */
export interface RelatedPersonaRef {
  slug: string;
  relation: string | null;
}

/**
 * Persona archetype as returned from `/api/personas`. Core fields shipped in
 * migration 106 (DC-1572); rich Pistoia fields added in migration 109 (DC-1606).
 * All rich fields are nullable — populated by the Pistoia seeder for
 * `source = 'pistoia'` rows; may be NULL for `'custom'`.
 */
export interface Persona {
  id: string;
  org_id: string;
  name: string;
  archetype: string | null;
  description: string | null;
  slug: string | null;
  source: PersonaSource | null;
  source_catalog_version: string | null;
  /**
   * `Record<pillar_key, narrative>`. Keys are open-set Pistoia sub-pillar
   * codes (F1/F2/F3/F4, A1/A1.1/A1.2/A2, I1/I2/I3, R1/R1.1/R1.2/R1.3, plus
   * future extensions). Read-only metadata.
   */
  pillar_benefits: Record<string, string> | null;
  /** VPC primitive: what the persona is trying to do (free-text prose). */
  persona_tasks: string | null;
  /** VPC primitive: pains / blockers. */
  issues: string | null;
  /** VPC primitive: upside FAIR enables. */
  latent_gains: string | null;
  /** Alternate role titles. */
  synonyms: string[] | null;
  /** Cross-persona graph. */
  related_personas: RelatedPersonaRef[] | null;
  /** Preamble prose framing FAIR for this persona. */
  fair_benefits_description: string | null;
  /** Un-truncated long-form description. */
  full_description: string | null;
  /** DC-1648: baseline governance role — drives baseline-gap risk scoring. */
  is_baseline: boolean;
  /** DC-1648: unassignment raises governance alerts. */
  is_critical: boolean;
  /** DC-1648: Producer/Enabler/Consumer grouping for the topology view. */
  primary_role: PrimaryRole | null;
  /** DC-1648: justification surfaced in governance-gap recommendations. */
  criticality_reason: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Document category mirror — kept narrow to the categories the client
 * cares about for rendering decisions. Group-source rendering branches
 * on `'group_transcript'` (DC-1602); other categories share the default
 * rendering path so they don't need to be enumerated here.
 */
export type DocumentCategory = string;

/**
 * Per-document attribution metadata for the Documented leg (DC-1602).
 * Returned by `GET /api/triangulation/domain/:domainId/documented-contributions`.
 *
 * For documents with `category === 'group_transcript'`, render with a
 * "Group Source" badge and session-level attribution
 * (`{document_name}, {formatDate(analyzed_at)}`) instead of any individual
 * persona attribution. Group-source contributions do not feed the
 * persona-weighted perceived leg.
 */
export interface DocumentedLegContribution {
  document_id: string;
  document_name: string;
  category: DocumentCategory;
  analyzed_at: string | null;
}

/**
 * One contributing source for the Perceived leg (DC-1628). Returned by
 * `GET /api/triangulation/domain/:domainId/perceived-contributions`.
 *
 * `source === 'interview'` is a 1:1 stakeholder interview (label = interviewee
 * role); `source === 'group_speaker'` is a mapped speaker from a group
 * transcript (label = speaker name, `session_name` = parent session/document).
 */
export interface PerceivedLegContribution {
  source: 'interview' | 'group_speaker';
  id: string;
  label: string;
  persona_id: string | null;
  session_name: string | null;
  date: string | null;
}

/**
 * One dataset/journey tagged to the domain for the Technical leg (DC-1628).
 * Returned by `GET /api/triangulation/domain/:domainId/technical-contributions`.
 * Includes datasets not yet evaluated (`evaluated === false`) so the view can
 * show which datasets were assessed — and which are pending — even when the
 * Technical leg reads Insufficient.
 */
export interface TechnicalLegContribution {
  journey_id: string;
  name: string;
  evaluated: boolean;
  overall_score: number | null;
}

// ============================================================================
// Prescriptive Triangulation playbooks (DC-1673)
// ----------------------------------------------------------------------------
// Mirrors the server's DomainPlaybookResolution. Returned by
// GET /api/triangulation/domain/:domainId/playbook.
// ============================================================================

export type PlaybookPatternFamily =
  | 'aligned'
  | 'soft_divergence'
  | 'hard_divergence'
  | 'neutral';

export type PlayDecisionAction = 'accept' | 'reject' | 'edit';

export interface PlaybookChecklistItem {
  seq: number;
  item: string;
}

/** One recommended play with the consultant's merged Accept/Reject/Edit state. */
export interface ResolvedPlay {
  seq: number;
  /** Effective title. Precedence: consultant edit > Bridge AI tailored > seed. */
  title: string;
  description: string;
  owner_role: string | null;
  /** Original seed text, retained so the UI can show "edited/tailored from …". */
  seed_title: string;
  seed_description: string;
  /** Active decision, or null when the play is still an undecided suggestion. */
  decision: PlayDecisionAction | null;
  decision_id: string | null;
  rationale: string | null;
  /** True when accepted or edited (i.e. flowing into the Action Plan). */
  in_action_plan: boolean;
  /** True when a Bridge AI tailored overlay exists for this play (DC-1639 L2). */
  tailored: boolean;
  /** Tailored effort (low|medium|high), null when not tailored. */
  effort: string | null;
  /** Tailored timeline (e.g. "2-4 weeks"), null when not tailored. */
  timeline: string | null;
  /** One-line note on the evidence the tailoring cited, null when not tailored. */
  evidence_summary: string | null;
}

export interface ResolvedPlaybook {
  pattern_name: PatternName;
  pattern_family: PlaybookPatternFamily;
  qualifier: PatternQualifier;
  pillars_fired: FairPillarName[];
  lowest_pillar_maturity: number;
  first_move: string;
  owner_role: string;
  common_misread: string;
  default_priority: 'critical' | 'high' | 'medium' | 'low';
  checklist: PlaybookChecklistItem[];
  plays: ResolvedPlay[];
  /** True when any play in this pattern has a Bridge AI tailored overlay. */
  tailored: boolean;
}

/**
 * Active owner assignment for a play (DC-1707 read-only surfacing). Mirrors
 * the server's `OwnerAssignmentView` fields the UI consumes — returned by
 * `GET /api/triangulation/domain/:domainId/owner-assignments` (a raw array,
 * active assignments only, keyed by the stable `(pattern_name, play_seq)`
 * play code).
 */
export interface PlayOwnerAssignment {
  pattern_name: PatternName;
  play_seq: number;
  owner_display_name: string;
  owner_kind: 'person' | 'team';
  owner_personas: Array<{ persona_id: string; name: string }>;
}

/**
 * A play projected from a Domain Pack rule (DC-1706). Mirrors the server's
 * `ResolvedPackPlay`.
 */
export interface ResolvedPackPlay {
  rule_id: string;
  rule_version: string;
  pack_ref: string;
  /** Effective title/description after overlay precedence. */
  title: string;
  description: string;
  /** Pack-seed text, pre-overlay. */
  seed_title: string;
  seed_description: string;
  severity: string;
  outcome: string;
  /** Datasets in the domain whose latest judgment fails THIS rule. */
  datasets_affected: number;
  /**
   * The judgments failing THIS rule — per rule, not the pack-wide union
   * (S5A).
   *
   * `ResolvedPackPlaybook.judgment_ids` is the union across every failing rule in the
   * pack. Attributing the union to one rule claims it affects datasets it passes on,
   * and contradicts the per-rule `datasets_affected` beside it.
   */
  judgment_ids: string[];
  regulatory_citations: Array<{ framework: string; reference: string; section?: string }>;
  effort: string | null;
  estimated_value: string | null;
  timeline: string | null;
  evidence_summary: string | null;
  decision: PlayDecisionAction | null;
  decision_id: string | null;
  rationale: string | null;
  in_action_plan: boolean;
  tailored: boolean;
}

/** Rule-sourced plays for one pack. Mirrors the server's `ResolvedPackPlaybook`. */
export interface ResolvedPackPlaybook {
  pack_ref: string;
  /** The PACK-level union. For per-rule scope read `ResolvedPackPlay.judgment_ids`. */
  judgment_ids: string[];
  plays: ResolvedPackPlay[];
}

export interface DomainPlaybookResolution {
  domain_id: string;
  domain_name: string;
  playbooks: ResolvedPlaybook[];
  /**
   * Rule-sourced plays from the domain's pack-based CV judgments (DC-1706).
   * Empty when no pack judgment exists.
   */
  pack_playbooks: ResolvedPackPlaybook[];
  pillars_with_pattern: number;
  /** True when a Bridge AI tailoring has been generated for this domain (DC-1639 L2). */
  tailored: boolean;
  /** ISO timestamp of the most recent tailoring, or null. */
  tailored_at: string | null;
  /** Model that produced the tailoring, or null. */
  tailored_model: string | null;
  /*
   * DELIBERATELY NO `active_decisions` HERE.
   *
   * The server keeps its active decision snapshot in an internal composite beside this
   * DTO, never inside it, because this object is serialized straight to the client and
   * `PlayDecisionRow` carries `org_id`, `decided_by`, `rationale` and frozen
   * accept-time text. Adding a decisions field here would re-open that leak.
   * See `playbookResolverSerialization.test.ts`.
   */
}

// ============================================================================
// REMEDIATION CANDIDATE — the S5 contract, FROZEN
// ============================================================================
//
// Mirrors `server/triangulation/remediationCandidate.ts`. Frozen after the H1-H10
// prototype gate cleared, which is what §7 of the tranche plan required before the
// external shape could be committed to.
//
// The candidate is the unified view of three populations that used to be handled
// separately, and two of which used to disappear:
//
//   live pattern plays   from triangulation cells
//   live pack plays      from Domain Pack CV judgments        (P0-5: never reached the plan)
//   stale acceptances    decided, evidence no longer firing   (P1-2: vanished silently)

/** Where a candidate came from. Stated, never inferred from a null field. */
export type CandidateOrigin = 'pattern' | 'pack_rule';

/**
 * Is the evidence behind this candidate still firing?
 *
 * `stale` is a decision the consultant made on evidence that no longer fires. It stays
 * in the plan and the exports, marked, and is never silently dropped.
 */
export type CandidateLifecycle = 'live' | 'stale';

/** Pack-rule identity. Present exactly when `origin === 'pack_rule'`. */
export interface RuleIdentity {
  rule_id: string;
  rule_version: string;
  /** Validated `name@x.y.z`. Part of the identity — rule ids are unique per pack only. */
  pack_ref: string;
}

/** Pattern-play identity. Present exactly when `origin === 'pattern'`. */
export interface PatternIdentity {
  pattern_name: string;
  play_seq: number;
}

/**
 * A regulatory citation carrying the rule identity that asserted it.
 *
 * The identity travels WITH the citation: a reference printed without the rule version
 * behind it cannot be checked against a pack later.
 */
export interface RemediationCitation {
  framework: string;
  reference: string;
  section?: string;
  rule_id: string;
  rule_version: string;
  pack_ref: string;
}

/**
 * Which datasets a candidate concerns.
 *
 * NULL for pattern candidates (a pattern fires on triangulation cells, not dataset
 * judgments) and for stale candidates (the rule no longer fires, so its former scope is
 * not a current fact). Reporting `datasets_affected: 0` instead would claim a
 * measurement.
 */
export interface DatasetScope {
  datasets_affected: number;
  judgment_ids: string[];
  domain_id: string;
}

/**
 * What is known about a candidate's evidence, in terms honest for each origin.
 *
 * NAMED `CandidateEvidenceBasis`, not `EvidenceBasis`: this module already exports an
 * `EvidenceBasis` string union meaning something entirely different — whether a
 * triangulation leg's documentation is full, declared-absent or missing. Two unrelated
 * concepts sharing one name in one module is how a consumer imports the wrong thing and
 * the compiler agrees with it.
 */
export interface CandidateEvidenceBasis {
  origin: CandidateOrigin;
  summary: string | null;
  /** Pack-only. */
  severity: string | null;
  /** Pack-only. */
  outcome: string | null;
  /** Pattern-only; empty for a pack rule. */
  pillars_fired: FairPillarName[];
  /** Pattern-only. */
  qualifier: PatternQualifier;
}

/** One decision in a candidate's history. */
export interface DecisionLineageEntry {
  decision_id: string;
  action: PlayDecisionAction;
  decided_by: string;
  decided_at: string;
  rationale: string | null;
  superseded_by: string | null;
  superseded_at: string | null;
  /** True for the row still in force. A trail that does not say which decision stands
   *  is not an audit trail. */
  is_current: boolean;
}

export interface CandidateAssignedOwner {
  display_name: string;
  kind: 'person' | 'team';
  personas: string[];
}

export interface RemediationCandidate {
  origin: CandidateOrigin;
  lifecycle: CandidateLifecycle;
  /**
   * REMEDIATION-CANDIDATE IDENTITY — not an Action Plan task identifier.
   *
   *     pattern:{pattern_name}:{play_seq}
   *     rule:{pack_ref}:{rule_id}@{rule_version}
   *
   * Used for candidate/source identity and Needs-review tray selection, because that is
   * the identity actually persisted for a decision. Journey Action Plan views key
   * selection on the canonical Action Plan task id instead — see
   * `client/types/actionPlan.ts`.
   */
  stable_key: string;
  rank: number;
  rule_identity: RuleIdentity | null;
  pattern_identity: PatternIdentity | null;
  title: string;
  description: string;
  seed_title: string | null;
  seed_description: string | null;
  citation: RemediationCitation[];
  dataset_scope: DatasetScope | null;
  evidence_basis: CandidateEvidenceBasis;
  decision_lineage: DecisionLineageEntry[];
  owner_role: string | null;
  assigned_owner: CandidateAssignedOwner | null;
  /**
   * NULL means UNCLASSIFIED, and is NOT `medium`.
   *
   * A stale candidate has no surviving urgency source, and an unfamiliar pack severity
   * is not evidence of medium priority. Render as "Priority unavailable" and count
   * separately.
   */
  /** Same vocabulary as `ResolvedPlaybook.default_priority`. */
  priority: 'critical' | 'high' | 'medium' | 'low' | null;
  effort: string | null;
  timeline: string | null;
  decision: PlayDecisionAction | null;
  tailored: boolean;
}
